import { getState } from '../state/store';
import Config from './config';

const HTTP_CODES = {
    SERVER_ERROR: 500,
    FORBIDDEN: 403
};

const responseHandler = async (response: any, onFail?: () => {}) => {
    if (response.status === HTTP_CODES.SERVER_ERROR) {
        if (onFail) return onFail();
    }

    if (response.status === HTTP_CODES.FORBIDDEN) {
        window.location.replace(Config.URL_COACHAPP);
        return;
    }

    let returnValue;
    try {
        returnValue = await response.text();
        returnValue = JSON.parse(returnValue);
    } catch (e) {
        returnValue = response;
    }

    if (
        returnValue &&
        typeof returnValue === 'object' &&
        !Array.isArray(returnValue) &&
        returnValue.status === undefined &&
        !response.ok
    ) {
        returnValue.status = response.status;
    }

    return returnValue;
};

const hasSessionToken = (token: string | null | undefined) =>
    Boolean(token && token !== 'null' && token !== 'undefined');

const NEEDS_REFRESH_HEADER = 'X-Needs-Refresh';

let refreshPromise: Promise<Response> | null = null;

const responseNeedsRefresh = (response: Response) => response.headers?.get?.(NEEDS_REFRESH_HEADER) === 'true';

const refreshAccessToken = () => {
    if (!refreshPromise) {
        refreshPromise = fetch(urlFormatter('/v5/auth/refresh'), {
            method: 'POST',
            credentials: 'include'
        }).finally(() => {
            refreshPromise = null;
        });
    }
    return refreshPromise;
};

const refreshFailed = async () => {
    try {
        const refreshResponse = await refreshAccessToken();
        return !refreshResponse.ok;
    } catch (error) {
        return true;
    }
};

const leaveAccount = () => {
    window.location.replace(Config.URL_COACHAPP);
};

const settleNeedsRefresh = async (response: Response, isRetry: boolean, retry: () => Promise<any>) => {
    if (isRetry || !responseNeedsRefresh(response)) {
        return null;
    }

    if (response.status === 401) {
        if (await refreshFailed()) {
            leaveAccount();
            return { refreshed: false };
        }
        return { refreshed: true, result: await retry() };
    }

    refreshAccessToken().catch(() => undefined);
    return null;
};

export async function authenticatedFetch(
    url: string,
    options: any = { method: 'GET' },
    onFail?: () => {},
    sessionToken?: string | null,
    isRetry = false
): Promise<any> {
    const resolvedSessionToken = sessionToken ?? getState().authentication.token;

    const authenticatedOptions = { ...options };

    if (!authenticatedOptions.headers) {
        authenticatedOptions.headers = {};
    }

    authenticatedOptions.headers['Api-Token'] = 'DISABLED';
    if (hasSessionToken(resolvedSessionToken)) {
        authenticatedOptions.headers['Session-Token'] = resolvedSessionToken;
    }
    authenticatedOptions.credentials = 'include';

    if (options.method !== 'GET' && authenticatedOptions.headers['Content-Type'] === undefined) {
        authenticatedOptions.headers['Content-Type'] = 'application/json';
    }

    let response;
    try {
        response = await fetch(urlFormatter(url), authenticatedOptions);
    } catch (error) {
        if (onFail) return onFail();
        return;
    }

    const refreshResult = await settleNeedsRefresh(response, isRetry, () =>
        authenticatedFetch(url, options, onFail, sessionToken, true)
    );
    if (refreshResult) {
        return refreshResult.result;
    }

    return responseHandler(response, onFail);
}

export async function unauthenticatedFetch(url: string, options: any = { method: 'GET' }) {
    const authenticatedOptions = { ...options };

    if (!authenticatedOptions.headers) {
        authenticatedOptions.headers = {};
    }

    if (options.method !== 'GET' && authenticatedOptions.headers['Content-Type'] === undefined) {
        authenticatedOptions.headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(urlFormatter(url), authenticatedOptions);

    return responseHandler(response);
}

export async function downloadFile(url: string, options: any = { method: 'GET' }, isRetry = false): Promise<any> {
    const sessionToken = getState().authentication.token;

    const authenticatedOptions = { ...options };

    if (!authenticatedOptions.headers) {
        authenticatedOptions.headers = {};
    }

    authenticatedOptions.headers['Api-Token'] = 'DISABLED';
    if (hasSessionToken(sessionToken)) {
        authenticatedOptions.headers['Session-Token'] = sessionToken;
    }
    authenticatedOptions.credentials = 'include';

    const response = await fetch(urlFormatter(url), authenticatedOptions);
    const refreshResult = await settleNeedsRefresh(response, isRetry, () => downloadFile(url, options, true));
    if (refreshResult) {
        return refreshResult.result;
    }

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    try {
        const blob = await response.blob();
        return { data: blob, status: 200, error: null, message: null };
    } catch (error) {
        console.error('downloadFile error:', error);
        throw error;
    }
}

export const urlFormatter = (path: string) => {
    if (path.indexOf('http://') === 0 || path.indexOf('https://') === 0) {
        return path;
    }
    return `${Config.API}${path}`;
};

export async function signOut() {
    if (hasSessionToken(getState().authentication.token)) {
        return;
    }

    await fetch(urlFormatter('/v5/auth/signOut'), {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json'
        }
    }).catch(() => undefined);
}
